import { DunningLevel, IFilterOperator, NotificationService, Status, callStated, callStatedList, emptyList, exportStated, scrollToTop, setListPaging, withCallState, withExportCallState, withListState } from '@aston/foundation';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { combineLatest, firstValueFrom, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { effect, inject, untracked } from '@angular/core';

import { DunningCasesService, DunningCasesListFilterSpecService } from '../services';
import { excludesDunningLevelFilter, filterOnLevelField } from '../functions';
import { ReferentialStore } from '../../shared-module/stores/referential';
import { MiddleFilterSpecService } from '../../shared-module/services';
import { IDunningCaseItem, IDunningCasesKpis } from '../models';
import { DunningCaseListSortProp } from '../enums';


const initialState = {
	cases: emptyList<IDunningCaseItem>(),
	kpis: null as IDunningCasesKpis,
	showInactiveCases: false,
}

export const CASES = 'cases';
export const KPIS = 'kpis';

export const DunningCasesStore = signalStore(
	{ providedIn: 'root', protectedState: false },

	withState(initialState),

	withListState<IDunningCaseItem, DunningCaseListSortProp, typeof CASES>({
		defaultSort: [{ prop: DunningCaseListSortProp.ExpiredOutstandingAmount, isDesc: true }],
		collection: CASES,
	}),
	withExportCallState({ collection: CASES }),

	withCallState({ collection: KPIS }),

	withMethods(store => {
		const specService = inject(DunningCasesListFilterSpecService);
		const filterSpecService = inject(MiddleFilterSpecService);
		const referential = inject(ReferentialStore);
		const service = inject(DunningCasesService);
		const notify = inject(NotificationService);

		const dunningLevels$ = toObservable(referential.dunningLevels);

		const loadCases = () => Promise.resolve(null)
			.then(() => cleanFilters())
			.then(params => callStatedList(service.getDunningCases({ ...params.paging, filters: params.filters }), store, CASES))
			.then(_ => {
				scrollToTop();
				loadKpis();
			});

		const loadKpis = () => callStated(service.getDunningCasesKpis(store.casesFilters()), store, KPIS);

		const cleanFilters = () => makeCleanFilters(store, specService.statusFieldFilterSpec)

		return {
			init: () => {
				loadCases();
				loadKpis();
			},
			loadCases,
			loadKpis,

			exportCases: () => Promise.resolve(null)
				.then(() => cleanFilters())
				.then(params => exportStated(service.exportDunningCases({ ...params.paging, filters: params.filters }), store, CASES))
				.catch(() => notify.error({ message: 'Errors.RetryableError', autoClose: true })),

			toggleInactiveCases: (showInactiveCases: boolean) => {
				patchState(store, { showInactiveCases });
				loadCases();
			},

			toggleDunningLevelFilter: (level: DunningLevel) => firstValueFrom(combineLatest([
				dunningLevels$,
				dunningLevels$.pipe(switchMap(levels => filterSpecService.getDunningLevelChoices(levels)))
			]))
				.then(([levels, choices]) => {
					const filter = filterOnLevelField(store.casesFilters());
					const choice = choices.find(c => c.value === level);
					const isActive = !excludesDunningLevelFilter(store.casesFilters(), level);
					const spec = specService.getDunningLevelFieldFilterSpec(levels);

					// Bug 34940: ACC [MO-Relance] Si on décoche un KPI qui modifie le nombre de page, la liste des dossiers n'est pas actualisée
					patchState(store, setListPaging({ page: 1 }, CASES))

					if (filter) {
						// Filter already exists.
						// Add the new value if previously inactive : Drop it if it was active
						const values = isActive ?
							filter.values.filter(l => l.value !== choice.value) :
							[ ...filter.values, choice ]

						// Remove the old one
						store.casesRemoveFilters([filter]);

						// Build the new one
						if (values.length) store.casesAddFilters([{ ...filter, values }]);
					}
					else {
						// Filter does not exist yet
						const levelFilter = {
							...specService.byLevelObjects(levels, [level]),
							values: [choice],
							spec,
						}
						store.casesAddFilters([levelFilter]);
					}
				})
		}
	}),

	withHooks({
		onInit: store => {
			effect(() => {
				const first = untracked(store.casesCallState) === 'init'
				store.casesPaging()
				store.casesFilters()
				// then
				if (!first) untracked(store.loadCases)
			})
		},
	})
);
export type DunningCasesStore = InstanceType<typeof DunningCasesStore>;

function makeCleanFilters(store, spec) {
	const otherFilters = [...store.casesFilters()];
	const paging = {...store.casesPaging()};

	const filters = store.showInactiveCases() ? otherFilters : [ ...otherFilters, {
		field: spec.field.value,
		operator: IFilterOperator.Equals,
		values: [Status.Enable],
		spec,
	} ];

	return { filters, paging };
}
